/**
 * Authorized QA Proxy add-on — FundGenius-owned QA domain routing only.
 * Never expose provider passwords in UI, logs, or generated launchers.
 */

import { getApiBaseUrl } from '../../utils/authUtils';
import { getStoredAccessToken } from '../authSession';
import {
  fundingHqFetch,
  getFundingApiErrorMessage,
  isFundingAuthError,
} from './fundingApi';

export const QA_PROXY_PROVIDER_DATAIMPULSE = 'dataimpulse';
export const QA_PROXY_PROVIDER_911PROXY = '911proxy';

export const AUTHORIZED_QA_PROXY_PROVIDERS = [
  { id: QA_PROXY_PROVIDER_DATAIMPULSE, label: 'DataImpulse' },
  { id: QA_PROXY_PROVIDER_911PROXY, label: '911Proxy' },
];

export const QA_PROXY_PROVIDER_SWITCH_MESSAGE =
  'Provider change applies to the next QA session. Your browser profile and saved session data are preserved.';

export function normalizeQaProxyProviderId(value) {
  const raw = String(value || '').trim().toLowerCase();
  return AUTHORIZED_QA_PROXY_PROVIDERS.some((item) => item.id === raw)
    ? raw
    : QA_PROXY_PROVIDER_DATAIMPULSE;
}

export function formatQaProxyProviderLabel(value) {
  const id = normalizeQaProxyProviderId(value);
  return AUTHORIZED_QA_PROXY_PROVIDERS.find((item) => item.id === id).label;
}

function normalizeStateVerification(raw) {
  if (!raw || typeof raw !== 'object') return null;
  return {
    requestedState: raw.requestedState ?? null,
    requestedStateLabel: raw.requestedStateLabel ?? null,
    providerTargeting: raw.providerTargeting ?? null,
    observedState: raw.observedState ?? null,
    observedStateLabel: raw.observedStateLabel ?? null,
    status: raw.status ?? null,
  };
}

export const AUTHORIZED_QA_PROXY_PROTOCOLS = [
  { id: 'http', label: 'HTTP/HTTPS' },
  { id: 'socks5', label: 'SOCKS5' },
];

export const QA_LOCATION_SOURCE_IDENTITY = 'identity_profile';
export const QA_LOCATION_SOURCE_MANUAL = 'manual_override';
export const QA_LOCATION_SOURCE_DEFAULT = 'default_targeting';

export const WEB_ACCESS_FULL_WEB = 'full_web';
export const WEB_ACCESS_RESTRICTED = 'restricted_allowlist';

/** US state abbreviation → display label (e.g. CA → California). */
export const US_STATE_ABBR_TO_LABEL = Object.freeze({
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', FL: 'Florida', GA: 'Georgia',
  HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa',
  KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland',
  MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri',
  MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
  NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio',
  OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina',
  SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont',
  VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
  DC: 'District of Columbia',
});

export function formatUsStateLabel(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const upper = raw.toUpperCase();
  if (US_STATE_ABBR_TO_LABEL[upper]) return US_STATE_ABBR_TO_LABEL[upper];
  // Slug "california" / "new-york" → title case
  if (raw.includes('-') || raw === raw.toLowerCase()) {
    return raw.split(/[-\s]+/).map((part) => (
      part ? part.charAt(0).toUpperCase() + part.slice(1).toLowerCase() : ''
    )).join(' ');
  }
  return raw;
}

export function formatWebAccessModeLabel(mode) {
  const value = String(mode || WEB_ACCESS_FULL_WEB).trim().toLowerCase();
  if (value === WEB_ACCESS_RESTRICTED || value === 'restricted') {
    return 'Restricted QA allowlist';
  }
  return 'Full web access through configured proxy';
}

export function formatQaLocationSourceLabel(source) {
  const value = String(source || '').trim().toLowerCase();
  if (value === QA_LOCATION_SOURCE_IDENTITY) return 'Client Personal Address';
  if (value === QA_LOCATION_SOURCE_MANUAL) return 'Manual override';
  return 'Default targeting';
}

/** Plain-language location source labels for the Browser Profiles QA card. */
export function formatLocationSourcePlain(source) {
  const normalized = String(source || '').trim().toLowerCase();
  if (normalized === QA_LOCATION_SOURCE_IDENTITY || normalized === 'identity_profile') {
    return 'Client Personal Address';
  }
  if (normalized === QA_LOCATION_SOURCE_MANUAL || normalized === 'manual_override') {
    return 'Manual override';
  }
  if (
    normalized === QA_LOCATION_SOURCE_DEFAULT
    || normalized === 'default_targeting'
    || !normalized
  ) {
    return 'Default QA location';
  }
  return formatQaLocationSourceLabel(source);
}

export function normalizeConnectionTestProof(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const target = raw.target && typeof raw.target === 'object' ? raw.target : {};
  const exit = raw.exit && typeof raw.exit === 'object' ? raw.exit : {};
  return {
    provider: raw.provider || null,
    directIp: raw.directIp ?? raw.direct_ip ?? null,
    proxyExitIp: raw.proxyExitIp ?? raw.proxy_exit_ip ?? null,
    differentIp: raw.differentIp ?? raw.different_ip ?? null,
    target: {
      country: target.country ?? null,
      state: target.state ?? null,
      city: target.city ?? null,
      zip: target.zip ?? null,
      asn: target.asn ?? null,
      mode: target.mode ?? null,
      label: stripQaSentinelText(target.label ?? null),
    },
    exit: {
      ip: exit.ip ?? null,
      country: exit.country ?? null,
      state: exit.state ?? exit.region ?? null,
      city: exit.city ?? null,
      postal: exit.postal ?? null,
      asn: exit.asn ?? null,
      available: Boolean(exit.available),
    },
    locationMatched: raw.locationMatched ?? raw.location_matched ?? null,
    locationStatus: raw.locationStatus ?? raw.location_status ?? null,
    networkStatus: raw.networkStatus ?? raw.network_status ?? null,
    locationVerification: raw.locationVerification || raw.location_verification || null,
    targetFingerprint: raw.targetFingerprint ?? raw.target_fingerprint ?? null,
    providerTargetReady: raw.providerTargetReady ?? raw.provider_target_ready ?? null,
    requestedTarget: raw.requestedTarget || raw.requested_target || null,
    latencyMs: raw.latencyMs ?? raw.latency_ms ?? null,
    testedAt: raw.testedAt ?? raw.tested_at ?? null,
    directIpSource: raw.directIpSource ?? raw.direct_ip_source ?? null,
    proxyExitIpSource: raw.proxyExitIpSource ?? raw.proxy_exit_ip_source ?? null,
    status: raw.status ?? null,
    ok: raw.ok ?? null,
    message: stripQaSentinelText(raw.message ?? null),
    geoEvidence: Array.isArray(raw.geoEvidence) ? raw.geoEvidence : null,
    providerRequest: raw.providerRequest && typeof raw.providerRequest === 'object'
      ? {
        targeting: String(raw.providerRequest.targeting || '')
          .split(';')
          .filter((part) => part && !/^sessid\./i.test(part))
          .join(';'),
        session: raw.providerRequest.session ?? null,
        sessionPresent: Boolean(raw.providerRequest.sessionPresent),
      }
      : null,
    providerTargetStatus: raw.providerTargetStatus ?? null,
    externalGeoStatus: raw.externalGeoStatus ?? null,
    fallback: normalizeLocationFallback(raw.fallback ?? raw.locationFallback ?? null),
    stateVerification: normalizeStateVerification(raw.stateVerification ?? raw.state_verification),
    errorCode: raw.errorCode ?? raw.error_code ?? null,
  };
}

export function normalizeLocationFallback(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const suggestions = Array.isArray(raw.nearbyProviderSupportedLocations)
    ? raw.nearbyProviderSupportedLocations
    : (Array.isArray(raw.nearby_provider_supported_locations)
      ? raw.nearby_provider_supported_locations
      : []);
  return {
    reason: raw.reason || null,
    reasonLabel: stripQaSentinelText(raw.reasonLabel ?? raw.reason_label ?? null),
    attemptCount: raw.attemptCount ?? raw.attempt_count ?? null,
    networkConfirmed: Boolean(raw.networkConfirmed ?? raw.network_confirmed),
    clientLocationSummary: stripQaSentinelText(
      raw.clientLocationSummary ?? raw.client_location_summary ?? null,
    ),
    nearbyProviderSupportedLocations: suggestions.map((item) => ({
      zip: item?.zip ?? null,
      city: item?.city ?? null,
      state: item?.state ?? null,
      stateProviderCode: item?.stateProviderCode ?? item?.state_provider_code ?? null,
      country: item?.country ?? null,
      distanceMiles: item?.distanceMiles ?? item?.distance_miles ?? null,
      distanceMethod: item?.distanceMethod ?? item?.distance_method ?? null,
      distanceConfidence: item?.distanceConfidence ?? item?.distance_confidence ?? null,
      zipType: item?.zipType ?? item?.zip_type ?? null,
      geometryConfidence: item?.geometryConfidence ?? item?.geometry_confidence ?? null,
      providerAvailable: item?.providerAvailable ?? item?.provider_available ?? true,
      providerSupported: item?.providerSupported ?? item?.provider_supported ?? true,
      availableIpCount: item?.availableIpCount ?? item?.available_ip_count ?? null,
      sameCity: Boolean(item?.sameCity ?? item?.same_city),
      label: item?.label || null,
      targeting: item?.targeting && typeof item.targeting === 'object'
        ? {
          mode: item.targeting.mode || QA_TARGETING_MODE_FILTERS,
          country: item.targeting.country || 'us',
          state: item.targeting.state || QA_TARGETING_ALL_STATES,
          city: item.targeting.city || QA_TARGETING_ALL_CITIES,
          zip: item.targeting.zip || QA_TARGETING_ALL_ZIPS,
          asn: item.targeting.asn || QA_TARGETING_ALL_ASNS,
        }
        : null,
    })),
    stateLevelSupported: Boolean(raw.stateLevelSupported ?? raw.state_level_supported),
    stateLevelTargeting: raw.stateLevelTargeting || raw.state_level_targeting || null,
  };
}

export const PROXY_BADGE_OFF = 'Off';
export const PROXY_BADGE_AUTHORIZED = 'Authorized QA';
export const PROXY_BADGE_SUSPENDED = 'Suspended';
export const PROXY_BADGE_QUOTA = 'Quota exhausted';
export const PROXY_BADGE_NOT_CONFIGURED = 'Not configured';

/** Fixed QA Proxy Session entrypoint — never chosen from localhost/staging/API lists in the UI. */
export const FIXED_AUTHORIZED_QA_PROXY_STARTUP_URL = 'https://app.fundelite.ai/';

export const PROXY_SAVE_FAILED_MESSAGE =
  'Could not save proxy configuration. Please check the fields and try again.';

export const TARGETED_TRAFFIC_COST_NOTICE =
  'Targeted traffic is billed by DataImpulse at 2× the standard traffic rate.';

export const QA_TARGETING_MODE_DEFAULT = 'default';
export const QA_TARGETING_MODE_FILTERS = 'target_filters';
export const QA_TARGETING_ALL_STATES = '__all_states__';
export const QA_TARGETING_ALL_CITIES = '__all_cities__';
export const QA_TARGETING_ALL_ZIPS = '__all_zips__';
export const QA_TARGETING_ALL_ASNS = '__all_asns__';

const QA_TARGETING_SENTINELS = new Set([
  QA_TARGETING_ALL_STATES,
  QA_TARGETING_ALL_CITIES,
  QA_TARGETING_ALL_ZIPS,
  QA_TARGETING_ALL_ASNS,
  'all',
  'all_states',
  'all_cities',
  'all_zips',
  'all_asns',
]);
const QA_SENTINEL_TOKEN_RE = /__all_[a-z]+__/gi;

/** True for empty values and provider "all" sentinels that must never render in UI. */
export function isQaTargetingSentinel(value) {
  const normalized = String(value ?? '').trim().toLowerCase();
  return !normalized || QA_TARGETING_SENTINELS.has(normalized);
}

/** Remove provider sentinel tokens (e.g. __all_cities__) from server-provided display text. */
export function stripQaSentinelText(text) {
  if (text == null) return text;
  const raw = String(text);
  if (!/__all_[a-z]+__/i.test(raw)) return raw;
  const cleaned = raw
    .replace(QA_SENTINEL_TOKEN_RE, '')
    .replace(/(?:\s*[·,]\s*){2,}/g, (match) => (match.includes('·') ? ' · ' : ', '))
    .replace(/:\s*[·,]\s*/g, ': ')
    .replace(/^\s*[·,]\s*/, '')
    .replace(/\s*[·,]\s*$/, '')
    .replace(/\s*[·,]\s*([.)])/g, '$1')
    .replace(/\s{2,}/g, ' ')
    .trim();
  return cleaned || null;
}

export const QA_BROWSER_DISPLAY_CURRENT = 'current';
export const QA_BROWSER_DISPLAY_OPTIONS = [
  { id: 'current', label: 'Current monitor' },
  { id: '1', label: 'Monitor 1' },
  { id: '2', label: 'Monitor 2' },
  { id: '3', label: 'Monitor 3' },
  { id: '4', label: 'Monitor 4' },
  { id: '5', label: 'Monitor 5' },
];

export function normalizeQaBrowserDisplay(value) {
  const raw = String(value || QA_BROWSER_DISPLAY_CURRENT).trim().toLowerCase();
  if (raw === '0' || raw === 'main' || raw === 'primary') return '1';
  if (raw === QA_BROWSER_DISPLAY_CURRENT || ['1', '2', '3', '4', '5'].includes(raw)) {
    return raw;
  }
  return QA_BROWSER_DISPLAY_CURRENT;
}

export const PROXY_GOOGLE_STARTUP_BLOCKED_MESSAGE =
  'This browser profile starts at Google, which is not an approved Authorized QA Proxy domain. Select an approved FundGenius QA or staging URL first.';

export const PROXY_STARTUP_URL_REQUIRED_MESSAGE =
  'Select an approved FundGenius QA or staging URL first.';

const PROXY_MANAGER_ROLES = new Set([
  'platform_owner',
  'platform_admin',
  'super_admin',
  'internal_admin',
  'owner',
  'admin',
  'agency_owner',
  'agency_admin',
]);

const JSON_HEADERS = Object.freeze({
  'Content-Type': 'application/json',
  Accept: 'application/json',
});

export function canManageAuthorizedQaProxy(user) {
  const role = String(user?.effective_role || user?.role || '').toLowerCase().trim();
  return PROXY_MANAGER_ROLES.has(role);
}

export function formatProxyBadgeLabel(badge) {
  const value = String(badge || PROXY_BADGE_OFF).trim() || PROXY_BADGE_OFF;
  return `Proxy: ${value}`;
}

/**
 * Compact client-summary status only (non-interactive).
 * Values: Configured | Not configured | Suspended
 */
export function formatAuthorizedQaProxyClientSummaryStatus(proxy) {
  const normalized = normalizeAuthorizedQaProxySummary(proxy) || proxy;
  const status = String(normalized?.status || '').toLowerCase();
  const badge = String(normalized?.badge || '').trim();
  const credentialsReady = Boolean(
    normalized?.credentialSaved
    || normalized?.credentialsConfigured
    || normalized?.providerCredentialsConfigured,
  );
  if (status === 'suspended' || badge === PROXY_BADGE_SUSPENDED) {
    return 'Suspended';
  }
  if (
    credentialsReady
    || status === 'active'
    || badge === PROXY_BADGE_AUTHORIZED
  ) {
    return 'Configured';
  }
  if (badge === PROXY_BADGE_NOT_CONFIGURED || badge === PROXY_BADGE_OFF || !badge) {
    return 'Not configured';
  }
  if (badge === PROXY_BADGE_QUOTA) {
    return 'Configured';
  }
  return 'Not configured';
}

export function formatBytesAllowance(remainingBytes, quotaBytes) {
  if (quotaBytes == null || quotaBytes <= 0) return 'No quota assigned';
  if (remainingBytes == null) return 'Allowance unavailable';
  const mb = remainingBytes / (1024 * 1024);
  const totalMb = quotaBytes / (1024 * 1024);
  if (totalMb >= 1024) {
    return `${(remainingBytes / (1024 * 1024 * 1024)).toFixed(2)} GB remaining of ${(totalMb / 1024).toFixed(2)} GB`;
  }
  return `${mb.toFixed(1)} MB remaining of ${totalMb.toFixed(1)} MB`;
}

export function resolveAuthorizedQaProxyLaunchConfigUrl() {
  const raw = String(process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
  const base = raw.endsWith('/api') ? raw.slice(0, -4) : raw;
  if (!base) {
    return 'https://api.fundelite.ai/api/funding-hq/authorized-qa-proxy/launch-config';
  }
  return `${base}/api/funding-hq/authorized-qa-proxy/launch-config`;
}

export function isGoogleStartupUrl(url) {
  try {
    const parsed = new URL(String(url || '').trim());
    const host = parsed.hostname.toLowerCase().replace(/^www\./, '');
    return host === 'google.com';
  } catch {
    return /google\.com/i.test(String(url || ''));
  }
}

/**
 * Normalize server-managed allowlist entries into selectable startup URLs.
 * Accepts either full URLs from the API or bare hosts as a fallback.
 */
export function buildApprovedProxyStartupUrlOptions(approvedStartupUrlsOrHosts = []) {
  const options = [];
  const seen = new Set();
  for (const entry of approvedStartupUrlsOrHosts || []) {
    const raw = String(entry || '').trim();
    if (!raw || raw.startsWith('*.')) continue;
    let url = raw;
    if (!/^https?:\/\//i.test(raw)) {
      const host = raw.toLowerCase().replace(/\/$/, '');
      url = (host === 'localhost' || host === '127.0.0.1')
        ? `http://${host}/`
        : `https://${host}/`;
    }
    if (seen.has(url)) continue;
    seen.add(url);
    options.push({ value: url, label: url });
  }
  return options;
}

export function isApprovedProxyStartupUrl(url, approvedStartupUrls = []) {
  const selected = String(url || '').trim();
  if (!selected) return false;
  return buildApprovedProxyStartupUrlOptions(approvedStartupUrls)
    .some((option) => option.value === selected);
}

export function getProxyLaunchTokenClientValidationMessage({
  profileStartupUrl,
  selectedProxyStartupUrl,
  approvedStartupUrls = [],
} = {}) {
  if (isApprovedProxyStartupUrl(selectedProxyStartupUrl, approvedStartupUrls)) {
    return null;
  }
  if (isGoogleStartupUrl(profileStartupUrl)) {
    return PROXY_GOOGLE_STARTUP_BLOCKED_MESSAGE;
  }
  return PROXY_STARTUP_URL_REQUIRED_MESSAGE;
}

function agencyQuery(agencyId) {
  return new URLSearchParams({ agency_id: agencyId }).toString();
}

/**
 * Build the Pydantic-compatible upsert payload.
 * Provider credentials are Admin-managed globally — never sent from client assignments.
 */
export function buildAuthorizedQaProxyUpsertPayload({
  provider,
  quotaBytes,
  status,
  targetingMode,
  targetingCountry,
  targetingState,
  targetingCity,
  targetingZip,
  targetingAsn,
  qaBrowserDisplay,
  qaLocationSource,
  resetToPersonalAddress,
} = {}) {
  const payload = {
    quota_bytes: Number(quotaBytes) || 0,
    status: status === 'suspended' ? 'suspended' : 'active',
    targeting_mode: targetingMode === QA_TARGETING_MODE_FILTERS
      ? QA_TARGETING_MODE_FILTERS
      : QA_TARGETING_MODE_DEFAULT,
    targeting_country: String(targetingCountry || 'us').trim().toLowerCase() || 'us',
    targeting_state: String(targetingState || QA_TARGETING_ALL_STATES).trim() || QA_TARGETING_ALL_STATES,
    targeting_city: String(targetingCity || QA_TARGETING_ALL_CITIES).trim() || QA_TARGETING_ALL_CITIES,
    targeting_zip: String(targetingZip || QA_TARGETING_ALL_ZIPS).trim() || QA_TARGETING_ALL_ZIPS,
    targeting_asn: String(targetingAsn || QA_TARGETING_ALL_ASNS).trim() || QA_TARGETING_ALL_ASNS,
    qa_browser_display: normalizeQaBrowserDisplay(qaBrowserDisplay),
  };
  // Omitted provider keeps the profile's saved provider; switching uses setAuthorizedQaProxyProvider.
  const providerId = String(provider || '').trim().toLowerCase();
  if (AUTHORIZED_QA_PROXY_PROVIDERS.some((item) => item.id === providerId)) {
    payload.provider = providerId;
  }
  const source = String(qaLocationSource || '').trim().toLowerCase();
  if (
    source === QA_LOCATION_SOURCE_IDENTITY
    || source === QA_LOCATION_SOURCE_MANUAL
    || source === QA_LOCATION_SOURCE_DEFAULT
  ) {
    payload.qa_location_source = source;
  }
  if (resetToPersonalAddress) {
    payload.reset_to_personal_address = true;
  }
  return payload;
}

/** Serialize a request body as a real JSON string for fetch (never a raw object). */
export function serializeAuthorizedQaProxyJsonBody(payload) {
  if (payload == null) {
    return '{}';
  }
  if (typeof payload === 'string') {
    // Guard against accidental double-stringify of an already-serialized object literal.
    const trimmed = payload.trim();
    if (
      (trimmed.startsWith('{') && trimmed.endsWith('}'))
      || (trimmed.startsWith('[') && trimmed.endsWith(']'))
    ) {
      JSON.parse(trimmed);
      return trimmed;
    }
  }
  return JSON.stringify(payload);
}

export function redactProxySecretsForLog(value, secrets = []) {
  let text = typeof value === 'string' ? value : JSON.stringify(value);
  for (const secret of secrets) {
    if (secret) {
      text = text.split(String(secret)).join('[REDACTED]');
    }
  }
  text = text.replace(/("password"\s*:\s*)"[^"]*"/gi, '$1"[REDACTED]"');
  text = text.replace(/(password=)[^\s&]+/gi, '$1[REDACTED]');
  return text;
}

export function logAuthorizedQaProxySaveFailure(error, secrets = []) {
  if (typeof console === 'undefined' || typeof console.error !== 'function') return;
  const detail = error?.data?.detail ?? error?.message ?? error;
  console.error(
    '[Authorized QA Proxy] save failed:',
    redactProxySecretsForLog(detail, secrets),
  );
}

export function isMalformedProxyJsonError(error) {
  const detail = error?.data?.detail;
  if (!Array.isArray(detail)) return false;
  return detail.some((item) => (
    item
    && typeof item === 'object'
    && (item.type === 'json_invalid' || String(item.msg || '').toLowerCase().includes('json decode'))
  ));
}

export function getAuthorizedQaProxySaveErrorMessage(error) {
  if (isFundingAuthError(error)) {
    return getFundingApiErrorMessage(error);
  }
  return PROXY_SAVE_FAILED_MESSAGE;
}

export function normalizeAuthorizedQaProxySummary(proxy) {
  if (!proxy || typeof proxy !== 'object') return null;
  const credentialsConfigured = Boolean(
    proxy.credentialsConfigured
    ?? proxy.credentials_configured
    ?? proxy.providerCredentialsConfigured
    ?? proxy.provider_credentials_configured
    ?? proxy.credentialSaved
    ?? proxy.credential_saved,
  );
  const credentialSaved = Boolean(
    proxy.credentialSaved ?? proxy.credential_saved ?? credentialsConfigured,
  );
  const providerSubaccountId = proxy.providerSubaccountId
    ?? proxy.provider_subaccount_id
    ?? '';
  const quotaBytes = proxy.quotaBytes ?? proxy.quota_bytes ?? null;
  const remainingBytes = proxy.remainingBytes ?? proxy.remaining_bytes ?? null;
  const usageBytes = proxy.usageBytes ?? proxy.usage_bytes ?? null;
  const fixedStartupUrl = proxy.fixedStartupUrl
    ?? proxy.fixed_startup_url
    ?? FIXED_AUTHORIZED_QA_PROXY_STARTUP_URL;
  const approvedHostsCount = proxy.approvedHostsCount
    ?? proxy.approved_hosts_count
    ?? null;
  const killSwitchEnabled = proxy.killSwitchEnabled
    ?? proxy.kill_switch_enabled
    ?? false;
  const lastConnectionTestStatus = proxy.lastConnectionTestStatus
    ?? proxy.last_connection_test_status
    ?? null;
  const lastConnectionTestAt = proxy.lastConnectionTestAt
    ?? proxy.last_connection_test_at
    ?? null;
  const lastConnectionTestProof = normalizeConnectionTestProof(
    proxy.lastConnectionTestProof ?? proxy.last_connection_test_proof ?? null,
  );
  const activeConnectionTestRaw = proxy.activeConnectionTest ?? proxy.active_connection_test ?? null;
  const activeConnectionTest = activeConnectionTestRaw
    && String(activeConnectionTestRaw.status || '').toLowerCase() === 'running'
    && (activeConnectionTestRaw.runId || activeConnectionTestRaw.run_id)
    ? {
      runId: String(activeConnectionTestRaw.runId || activeConnectionTestRaw.run_id),
      status: 'running',
      startedAt: activeConnectionTestRaw.startedAt || activeConnectionTestRaw.started_at || null,
    }
    : null;
  const usageEstimated = proxy.usageEstimated ?? proxy.usage_estimated ?? false;
  const targetingRaw = proxy.targeting && typeof proxy.targeting === 'object'
    ? proxy.targeting
    : {};
  const targeting = {
    mode: targetingRaw.mode === QA_TARGETING_MODE_FILTERS
      ? QA_TARGETING_MODE_FILTERS
      : QA_TARGETING_MODE_DEFAULT,
    country: String(targetingRaw.country || 'us').toLowerCase(),
    countryLabel: targetingRaw.countryLabel || targetingRaw.country_label || 'United States',
    state: targetingRaw.state || QA_TARGETING_ALL_STATES,
    city: targetingRaw.city || QA_TARGETING_ALL_CITIES,
    zip: targetingRaw.zip || QA_TARGETING_ALL_ZIPS,
    asn: targetingRaw.asn || QA_TARGETING_ALL_ASNS,
    usesTargetFilters: Boolean(
      targetingRaw.usesTargetFilters ?? targetingRaw.uses_target_filters,
    ),
    costNotice: targetingRaw.costNotice
      || targetingRaw.cost_notice
      || (targetingRaw.usesTargetFilters ? TARGETED_TRAFFIC_COST_NOTICE : null),
    provider: targetingRaw.provider || 'dataimpulse',
  };

  const webAccessMode = String(
    proxy.webAccessMode ?? proxy.web_access_mode ?? WEB_ACCESS_FULL_WEB,
  ).trim().toLowerCase() || WEB_ACCESS_FULL_WEB;
  const host = proxy.host || null;
  const port = proxy.port ?? null;
  const connectionHostPort = proxy.connectionHostPort
    ?? proxy.connection_host_port
    ?? (host && port != null ? `${host}:${port}` : (host || null));
  const locationSource = proxy.locationSource
    ?? proxy.location_source
    ?? proxy.qaLocationSource
    ?? proxy.qa_location_source
    ?? QA_LOCATION_SOURCE_DEFAULT;
  const geoTargetLabel = proxy.geoTargetLabel
    ?? proxy.geo_target_label
    ?? null;
  const defaultProvider = normalizeQaProxyProviderId(
    proxy.defaultProvider ?? proxy.default_provider ?? proxy.provider,
  );
  const launchTargetReady = proxy.launchTargetReady ?? proxy.launch_target_ready ?? null;
  const state911Ready = defaultProvider === QA_PROXY_PROVIDER_911PROXY && launchTargetReady != null
    ? Boolean(launchTargetReady)
    : null;

  // Strip any allowlist URL lists — clients never choose among localhost/staging/API URLs.
  const {
    approvedStartupUrls: _approvedStartupUrls,
    approved_startup_urls: _approvedStartupUrlsSnake,
    ...rest
  } = proxy;

  return {
    ...rest,
    credentialSaved,
    credentialsConfigured,
    providerCredentialsConfigured: credentialsConfigured,
    providerManagedByAdmin: Boolean(
      proxy.providerManagedByAdmin ?? proxy.provider_managed_by_admin ?? true,
    ),
    provider: proxy.provider || null,
    defaultProvider,
    providerLabel: formatQaProxyProviderLabel(defaultProvider),
    providerActive: proxy.providerActive ?? proxy.provider_active ?? null,
    providerCapabilities: proxy.providerCapabilities ?? proxy.provider_capabilities ?? null,
    availableProviders: Array.isArray(proxy.availableProviders ?? proxy.available_providers)
      ? (proxy.availableProviders ?? proxy.available_providers)
        .filter((item) => item && AUTHORIZED_QA_PROXY_PROVIDERS.some((p) => p.id === item.id))
        .map((item) => ({
          id: item.id,
          label: formatQaProxyProviderLabel(item.id),
          configured: Boolean(item.configured),
          active: Boolean(item.active),
        }))
      : null,
    launchTargetReady,
    protocol: proxy.protocol || null,
    host,
    port,
    connectionHostPort,
    providerSubaccountId,
    quotaBytes,
    remainingBytes,
    usageBytes,
    usageEstimated,
    fixedStartupUrl,
    approvedHostsCount,
    killSwitchEnabled,
    lastConnectionTestStatus,
    lastConnectionTestAt,
    lastConnectionTestProof,
    activeConnectionTest,
    status: proxy.status || null,
    badge: proxy.badge || null,
    entitled: proxy.entitled !== false,
    targeting,
    webAccessMode,
    policySource: proxy.policySource
      ?? proxy.policy_source
      ?? 'Admin Control Center',
    locationSource,
    qaLocationSource: locationSource,
    qaRequestedTargeting: (() => {
      const requested = proxy.qaRequestedTargeting
        ?? proxy.qa_requested_targeting
        ?? null;
      if (!requested || typeof requested !== 'object') return null;
      return {
        mode: requested.mode === QA_TARGETING_MODE_FILTERS
          ? QA_TARGETING_MODE_FILTERS
          : (requested.mode || QA_TARGETING_MODE_DEFAULT),
        country: String(requested.country || 'us').toLowerCase(),
        state: requested.state || QA_TARGETING_ALL_STATES,
        city: requested.city || QA_TARGETING_ALL_CITIES,
        zip: requested.zip || QA_TARGETING_ALL_ZIPS,
        asn: requested.asn || QA_TARGETING_ALL_ASNS,
      };
    })(),
    // 911Proxy is state-only: DataImpulse ZIP/city availability never gates its launches.
    providerTargetReady: state911Ready ?? (proxy.providerTargetReady ?? proxy.provider_target_ready ?? true),
    providerTargetStatus: state911Ready != null
      ? (state911Ready ? 'active' : 'selection_required')
      : (proxy.providerTargetStatus
        ?? proxy.provider_target_status
        ?? ((proxy.providerTargetReady ?? proxy.provider_target_ready ?? true)
          ? 'active'
          : 'selection_required')),
    locationSelectionRequired: (() => {
      if (state911Ready != null) return null;
      const raw = proxy.locationSelectionRequired
        ?? proxy.location_selection_required
        ?? null;
      if (!raw || typeof raw !== 'object') return null;
      return {
        required: raw.required !== false,
        reason: raw.reason || 'requested_location_unavailable',
        requestedTargeting: raw.requestedTargeting || raw.requested_targeting || null,
        fallback: normalizeLocationFallback(raw.fallback),
      };
    })(),
    qaOriginalRequestedTargeting: (() => {
      const original = proxy.qaOriginalRequestedTargeting
        ?? proxy.qa_original_requested_targeting
        ?? null;
      if (!original || typeof original !== 'object') return null;
      return {
        mode: original.mode === QA_TARGETING_MODE_FILTERS
          ? QA_TARGETING_MODE_FILTERS
          : (original.mode || QA_TARGETING_MODE_DEFAULT),
        country: String(original.country || 'us').toLowerCase(),
        countryLabel: original.countryLabel || original.country_label || null,
        state: original.state || QA_TARGETING_ALL_STATES,
        city: original.city || QA_TARGETING_ALL_CITIES,
        zip: original.zip || QA_TARGETING_ALL_ZIPS,
        asn: original.asn || QA_TARGETING_ALL_ASNS,
        usesTargetFilters: Boolean(
          original.usesTargetFilters ?? original.uses_target_filters,
        ),
      };
    })(),
    geoTargetLabel,
    privateNetworkAccessBlocked: Boolean(
      proxy.privateNetworkAccessBlocked ?? proxy.private_network_access_blocked ?? true,
    ),
    primaryBrowserDirect: Boolean(
      proxy.primaryBrowserDirect ?? proxy.primary_browser_direct ?? true,
    ),
    qaBrowserAlwaysProxied: Boolean(
      proxy.qaBrowserAlwaysProxied ?? proxy.qa_browser_always_proxied ?? true,
    ),
    qaBrowserDisplay: normalizeQaBrowserDisplay(
      proxy.qaBrowserDisplay ?? proxy.qa_browser_display,
    ),
    // Stable identity refs for save/reload diagnostics (never secrets).
    id: proxy.id ?? null,
    clientId: proxy.clientId ?? proxy.client_id ?? null,
    agencyId: proxy.agencyId ?? proxy.agency_id ?? null,
    profileId: proxy.profileId ?? proxy.profile_id ?? null,
  };
}

export function formatAuthorizedQaProxyConnectionLabel(proxy) {
  const normalized = normalizeAuthorizedQaProxySummary(proxy);
  const credentialsReady = Boolean(
    normalized?.credentialsConfigured || normalized?.credentialSaved,
  );
  if (!credentialsReady && normalized?.status !== 'active') {
    return 'Not configured';
  }
  const providerLabel = normalized.providerLabel;
  if (normalized.defaultProvider === QA_PROXY_PROVIDER_911PROXY) {
    return `${providerLabel} · Fresh endpoint per launch`;
  }
  const connection = normalized.connectionHostPort
    || (
      normalized.host && normalized.port != null
        ? `${normalized.host}:${normalized.port}`
        : (normalized.host || '')
    );
  if (connection) return `${providerLabel} · ${connection}`;
  return `${providerLabel} · Managed by Admin`;
}

export function formatAuthorizedQaProxyRoutingStatus(proxy) {
  const normalized = normalizeAuthorizedQaProxySummary(proxy);
  const status = String(normalized?.status || '').toLowerCase();
  const badge = String(normalized?.badge || '').trim();
  const credentialsReady = Boolean(
    normalized?.credentialSaved
    || normalized?.credentialsConfigured
    || normalized?.providerCredentialsConfigured,
  );
  if (status === 'suspended' || badge === PROXY_BADGE_SUSPENDED) {
    return 'Suspended';
  }
  if (
    credentialsReady
    || status === 'active'
    || badge === PROXY_BADGE_AUTHORIZED
    || badge === PROXY_BADGE_QUOTA
  ) {
    return status === 'active' || badge === PROXY_BADGE_AUTHORIZED
      ? 'Configured / Active'
      : 'Configured';
  }
  return 'Not configured';
}

export async function getAuthorizedQaProxy({ clientId, agencyId, profileId }) {
  try {
    const summary = await fundingHqFetch(
      `/clients/${encodeURIComponent(clientId)}/browser-profiles/${encodeURIComponent(profileId)}/authorized-qa-proxy?${agencyQuery(agencyId)}`,
    );
    return normalizeAuthorizedQaProxySummary(summary);
  } catch (error) {
    throw new Error(getFundingApiErrorMessage(error, 'Unable to load Authorized QA Proxy status.'));
  }
}

/** Latest browser-verified 911Proxy QA Browser session for this profile, or null. This is
 * the live session — never the connection test. */
export async function getAuthorizedQaCurrentSession({ clientId, agencyId, profileId }) {
  try {
    const body = await fundingHqFetch(
      `/clients/${encodeURIComponent(clientId)}/browser-profiles/${encodeURIComponent(profileId)}/authorized-qa-proxy/current-session?${agencyQuery(agencyId)}`,
    );
    return body && typeof body.session === 'object' ? body.session : null;
  } catch (error) {
    throw new Error(getFundingApiErrorMessage(error, 'Unable to load the current QA Browser session.'));
  }
}

export async function getAuthorizedQaProxyLocationOptions({
  agencyId,
  country = 'us',
  state = '',
} = {}) {
  try {
    const params = new URLSearchParams({
      agency_id: agencyId,
      country: String(country || 'us'),
    });
    if (state) params.set('state', String(state));
    return await fundingHqFetch(
      `/authorized-qa-proxy/location-options?${params.toString()}`,
    );
  } catch (error) {
    throw new Error(getFundingApiErrorMessage(error, 'Unable to load DataImpulse location options.'));
  }
}

export function normalizeTargetingCatalogPayload(payload) {
  if (!payload || typeof payload !== 'object') {
    return { imported: false, catalog: null };
  }
  if (Array.isArray(payload.results) && payload.imported !== true) {
    return { imported: false, catalog: null };
  }
  const catalog = payload.catalog
    || (payload.catalogs ? payload : null);
  if (payload.imported === false || catalog == null) {
    return { imported: false, catalog: null };
  }
  const catalogs = catalog.catalogs || {};
  const hasRows = ['state', 'city', 'zip'].some((kind) => {
    const rowCount = catalogs[kind]?.rowCount ?? catalogs[kind]?.row_count;
    return Number(rowCount) > 0;
  });
  if (!hasRows && payload.imported !== true) {
    return { imported: false, catalog: null };
  }
  return { imported: true, catalog };
}

export async function getAuthorizedQaProxyTargetingCatalog({ agencyId } = {}) {
  try {
    const query = agencyQuery(agencyId);
    let payload;
    try {
      payload = await fundingHqFetch(
        `/authorized-qa-proxy/targeting-catalog/current?${query}`,
      );
    } catch (error) {
      if (error?.status !== 404) throw error;
      payload = await fundingHqFetch(
        `/authorized-qa-proxy/targeting-catalog?${query}`,
      );
    }
    return normalizeTargetingCatalogPayload(payload);
  } catch (error) {
    throw new Error(getFundingApiErrorMessage(error, 'Unable to load the location catalog.'));
  }
}

function catalogSearchPath(kind, { agencyId, q = '', state = '', city = '', limit = 25 } = {}) {
  const params = new URLSearchParams({
    agency_id: agencyId,
    q: String(q || ''),
    limit: String(limit || 25),
  });
  if (state) params.set('state', String(state));
  if (city) params.set('city', String(city));
  return `/authorized-qa-proxy/targeting-catalog/${kind}?${params.toString()}`;
}

export async function searchAuthorizedQaProxyTargetingStates(options = {}) {
  try {
    return await fundingHqFetch(catalogSearchPath('states', options));
  } catch (error) {
    throw new Error(getFundingApiErrorMessage(error, 'Unable to search DataImpulse states.'));
  }
}

export async function searchAuthorizedQaProxyTargetingCities(options = {}) {
  try {
    return await fundingHqFetch(catalogSearchPath('cities', options));
  } catch (error) {
    throw new Error(getFundingApiErrorMessage(error, 'Unable to search DataImpulse cities.'));
  }
}

export async function searchAuthorizedQaProxyTargetingZips(options = {}) {
  try {
    return await fundingHqFetch(catalogSearchPath('zips', options));
  } catch (error) {
    throw new Error(getFundingApiErrorMessage(error, 'Unable to search DataImpulse ZIP codes.'));
  }
}

/** Search ASN options only. Never queries State, City, or ZIP catalogs. */
export function searchAuthorizedQaProxyTargetingAsns({ asns = [], q = '' } = {}) {
  const needle = String(q || '').trim().toLowerCase();
  if (!needle) return { results: [] };
  const results = [];
  for (const item of asns || []) {
    const code = String(item?.code || item?.providerCode || '').trim();
    if (!code || code === QA_TARGETING_ALL_ASNS) continue;
    const providerName = item.name || item.providerName || '';
    const haystack = `${code} ${item.label || ''} ${providerName}`.toLowerCase();
    if (!haystack.includes(needle)) continue;
    results.push({
      providerCode: code,
      displayName: item.label || code,
      providerName,
      availableIpCount: item.availableIpCount ?? item.available_ip_count ?? null,
    });
    if (results.length >= 25) break;
  }
  if (/^\d{1,10}$/.test(needle) && !results.some((row) => row.providerCode === needle)) {
    results.unshift({
      providerCode: needle,
      displayName: needle,
      providerName: '',
      availableIpCount: null,
    });
  }
  return { results };
}

export function formatTargetingLocationSummary(targeting, { geoTargetLabel } = {}) {
  if (geoTargetLabel) return stripQaSentinelText(String(geoTargetLabel));
  if (!targeting || typeof targeting !== 'object') return null;
  const country = targeting.countryLabel || targeting.country_label
    || targeting.country || 'United States';
  const mode = targeting.mode;
  const hasState = !isQaTargetingSentinel(targeting.state);
  const hasCity = !isQaTargetingSentinel(targeting.city);
  const hasZip = !isQaTargetingSentinel(targeting.zip);
  const hasAsn = !isQaTargetingSentinel(targeting.asn);
  const hasFilters = mode === QA_TARGETING_MODE_FILTERS
    || targeting.usesTargetFilters
    || hasState || hasCity || hasZip || hasAsn;
  if (!hasFilters) {
    return null;
  }
  const parts = [country === 'us' || country === 'US' ? 'United States' : country];
  if (hasState) parts.push(formatUsStateLabel(targeting.state));
  if (hasCity) parts.push(targeting.city);
  if (hasZip) parts.push(`ZIP ${targeting.zip}`);
  if (hasAsn) parts.push(`ASN ${targeting.asn}`);
  return parts.join(' · ');
}

/** Compact city/state/ZIP label for selection-required UI (e.g. "San Francisco, CA 94118"). */
export function formatCompactQaLocationLabel(targeting) {
  if (!targeting || typeof targeting !== 'object') return null;
  const zip = !isQaTargetingSentinel(targeting.zip) ? String(targeting.zip).trim() : '';
  const cityRaw = !isQaTargetingSentinel(targeting.city) ? String(targeting.city).trim() : '';
  const city = cityRaw
    ? cityRaw.replace(/\b\w/g, (ch) => ch.toUpperCase()).replace(/-/g, ' ')
    : '';
  const state = !isQaTargetingSentinel(targeting.state) ? formatUsStateLabel(targeting.state) : '';
  if (city && state && zip) return `${city}, ${state} ${zip}`;
  if (city && zip) return `${city} ${zip}`;
  if (state && zip) return `${state} · ZIP ${zip}`;
  if (zip) return `ZIP ${zip}`;
  if (city && state) return `${city}, ${state}`;
  if (state) return state;
  return formatTargetingLocationSummary(targeting);
}

export function formatQaTestLocationSummary(proxy) {
  const normalized = normalizeAuthorizedQaProxySummary(proxy);
  if (normalized?.geoTargetLabel) {
    const label = stripQaSentinelText(String(normalized.geoTargetLabel));
    if (label) return label;
  }

  const selectionRequired = normalized?.providerTargetReady === false
    || String(normalized?.providerTargetStatus || '').toLowerCase() === 'selection_required'
    || Boolean(normalized?.locationSelectionRequired?.required);

  const requested = normalized?.locationSelectionRequired?.requestedTargeting
    || normalized?.qaRequestedTargeting
    || normalized?.qaOriginalRequestedTargeting
    || null;

  // Selection required: show the requested client location (never invent US default).
  if (selectionRequired) {
    const compact = formatCompactQaLocationLabel(requested);
    if (compact) return compact;
    const requestedLabel = formatTargetingLocationSummary(requested);
    if (requestedLabel) return requestedLabel;
    return 'Location selection required';
  }

  // Active provider target ready: prefer active targeting (e.g. nearby override ZIP).
  const targeting = normalized?.targeting;
  const activeCompact = formatCompactQaLocationLabel(targeting);
  if (activeCompact) return activeCompact;
  const activeLabel = formatTargetingLocationSummary(targeting, {
    geoTargetLabel: null,
  });
  if (activeLabel) return activeLabel;

  // Legacy: active still default but a concrete requested ZIP exists.
  const requestedFallback = formatCompactQaLocationLabel(requested)
    || formatTargetingLocationSummary(requested);
  if (requestedFallback) return requestedFallback;

  if (!targeting) return 'United States (default)';
  const country = targeting.countryLabel || targeting.country || 'United States';
  return `${country} (default targeting)`;
}

export function isQaProviderTargetSelectionRequired(proxy) {
  const normalized = normalizeAuthorizedQaProxySummary(proxy) || proxy || {};
  return normalized.providerTargetReady === false
    || String(normalized.providerTargetStatus || '').toLowerCase() === 'selection_required'
    || Boolean(normalized.locationSelectionRequired?.required);
}

/** Truthful proximity copy — never claim exact miles for low-confidence geometry. */
export function formatSuggestionDistanceHint(suggestion) {
  if (!suggestion) return 'Provider-supported';
  const miles = suggestion.distanceMiles;
  if (miles == null || Number.isNaN(Number(miles))) {
    return 'Provider-supported · distance unavailable';
  }
  const formatted = Number.isInteger(miles) ? String(miles) : String(miles);
  return `Approx. ${formatted} mi from client location`;
}

export async function getAuthorizedQaProxyLocationFromIdentity({
  clientId,
  agencyId,
} = {}) {
  try {
    const params = new URLSearchParams({ agency_id: agencyId });
    return await fundingHqFetch(
      `/clients/${encodeURIComponent(clientId)}/authorized-qa-proxy/location-from-identity?${params.toString()}`,
    );
  } catch (error) {
    throw new Error(
      getFundingApiErrorMessage(error, 'Unable to resolve QA location from personal address.'),
    );
  }
}

export async function getAuthorizedQaProxyNearbyLocations({
  clientId,
  agencyId,
  profileId,
  country,
  state,
  city,
  zip,
  limit = 5,
} = {}) {
  try {
    const params = new URLSearchParams({ agency_id: agencyId });
    if (profileId) params.set('profile_id', profileId);
    if (country) params.set('country', country);
    if (state) params.set('state', state);
    if (city) params.set('city', city);
    if (zip) params.set('zip', zip);
    if (limit) params.set('limit', String(limit));
    const raw = await fundingHqFetch(
      `/clients/${encodeURIComponent(clientId)}/authorized-qa-proxy/nearby-locations?${params.toString()}`,
    );
    const suggestions = Array.isArray(raw?.suggestions) ? raw.suggestions : [];
    return {
      requested: raw?.requested || null,
      catalogPresent: Boolean(raw?.catalogPresent ?? raw?.catalog_present),
      suggestions: suggestions.map((item) => ({
        zip: item?.zip ?? null,
        city: item?.city ?? null,
        state: item?.state ?? null,
        stateProviderCode: item?.stateProviderCode ?? item?.state_provider_code ?? null,
        country: item?.country ?? null,
        distanceMiles: item?.distanceMiles ?? item?.distance_miles ?? null,
        distanceMethod: item?.distanceMethod ?? item?.distance_method ?? null,
        distanceConfidence: item?.distanceConfidence ?? item?.distance_confidence ?? null,
        zipType: item?.zipType ?? item?.zip_type ?? null,
        geometryConfidence: item?.geometryConfidence ?? item?.geometry_confidence ?? null,
        providerAvailable: item?.providerAvailable ?? item?.provider_available ?? true,
        providerSupported: item?.providerSupported ?? item?.provider_supported ?? true,
        availableIpCount: item?.availableIpCount ?? item?.available_ip_count ?? null,
        sameCity: Boolean(item?.sameCity ?? item?.same_city),
        label: item?.label || null,
        targeting: item?.targeting && typeof item.targeting === 'object'
          ? {
            mode: item.targeting.mode || QA_TARGETING_MODE_FILTERS,
            country: item.targeting.country || 'us',
            state: item.targeting.state || QA_TARGETING_ALL_STATES,
            city: item.targeting.city || QA_TARGETING_ALL_CITIES,
            zip: item.targeting.zip || QA_TARGETING_ALL_ZIPS,
            asn: item.targeting.asn || QA_TARGETING_ALL_ASNS,
          }
          : null,
      })),
      stateLevelSupported: Boolean(raw?.stateLevelSupported ?? raw?.state_level_supported),
      stateProviderCode: raw?.stateProviderCode ?? raw?.state_provider_code ?? '',
      origin: raw?.origin || null,
      fallback: normalizeLocationFallback(raw?.fallback),
      candidatesConsidered: raw?.candidatesConsidered ?? raw?.candidates_considered ?? 0,
    };
  } catch (error) {
    throw new Error(
      getFundingApiErrorMessage(error, 'Unable to load nearby provider-supported locations.'),
    );
  }
}

export async function upsertAuthorizedQaProxy({
  clientId,
  agencyId,
  profileId,
  provider,
  quotaBytes,
  status,
  targetingMode,
  targetingCountry,
  targetingState,
  targetingCity,
  targetingZip,
  targetingAsn,
  qaBrowserDisplay,
  qaLocationSource,
  resetToPersonalAddress,
}) {
  const payload = buildAuthorizedQaProxyUpsertPayload({
    provider,
    quotaBytes,
    status,
    targetingMode,
    targetingCountry,
    targetingState,
    targetingCity,
    targetingZip,
    targetingAsn,
    qaBrowserDisplay,
    qaLocationSource,
    resetToPersonalAddress,
  });
  const body = serializeAuthorizedQaProxyJsonBody(payload);
  try {
    const saved = await fundingHqFetch(
      `/clients/${encodeURIComponent(clientId)}/browser-profiles/${encodeURIComponent(profileId)}/authorized-qa-proxy?${agencyQuery(agencyId)}`,
      {
        method: 'PUT',
        headers: JSON_HEADERS,
        body,
      },
    );
    return normalizeAuthorizedQaProxySummary(saved);
  } catch (error) {
    logAuthorizedQaProxySaveFailure(error, []);
    throw new Error(getAuthorizedQaProxySaveErrorMessage(error));
  }
}

/** Change only the upstream provider for the profile's next QA launch. */
export async function setAuthorizedQaProxyProvider({ clientId, agencyId, profileId, provider }) {
  const providerId = String(provider || '').trim().toLowerCase();
  if (!AUTHORIZED_QA_PROXY_PROVIDERS.some((item) => item.id === providerId)) {
    throw new Error('Unsupported QA proxy provider.');
  }
  try {
    const saved = await fundingHqFetch(
      `/clients/${encodeURIComponent(clientId)}/browser-profiles/${encodeURIComponent(profileId)}/authorized-qa-proxy/provider?${agencyQuery(agencyId)}`,
      {
        method: 'PUT',
        headers: JSON_HEADERS,
        body: serializeAuthorizedQaProxyJsonBody({ provider: providerId }),
      },
    );
    const summary = normalizeAuthorizedQaProxySummary(saved);
    return {
      ...summary,
      providerChangeMessage: saved?.providerChangeMessage || QA_PROXY_PROVIDER_SWITCH_MESSAGE,
    };
  } catch (error) {
    throw new Error(getFundingApiErrorMessage(error, 'Unable to change the QA proxy provider.'));
  }
}

export function createConnectionTestRunId() {
  const cryptoApi = typeof globalThis !== 'undefined' ? globalThis.crypto : null;
  if (cryptoApi && typeof cryptoApi.randomUUID === 'function') {
    return `qct_${cryptoApi.randomUUID().replace(/-/g, '')}`;
  }
  return `qct_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
}

export function isConnectionTestAbortError(error) {
  return Boolean(error) && error.name === 'AbortError';
}

export async function testAuthorizedQaProxy({
  clientId,
  agencyId,
  profileId,
  clientDirectIp,
  runId,
  signal,
}) {
  try {
    const payload = {};
    if (clientDirectIp) {
      payload.client_direct_ip = String(clientDirectIp).trim();
    }
    if (runId) {
      payload.run_id = String(runId);
    }
    return await fundingHqFetch(
      `/clients/${encodeURIComponent(clientId)}/browser-profiles/${encodeURIComponent(profileId)}/authorized-qa-proxy/test?${agencyQuery(agencyId)}`,
      {
        method: 'POST',
        headers: JSON_HEADERS,
        body: serializeAuthorizedQaProxyJsonBody(payload),
        signal,
      },
    );
  } catch (error) {
    if (isConnectionTestAbortError(error)) throw error;
    throw new Error(getFundingApiErrorMessage(error, 'Unable to test Authorized QA Proxy connection.'));
  }
}

/** Server-side termination of a running connection test. Idempotent. */
export async function cancelAuthorizedQaProxyTest({
  clientId,
  agencyId,
  profileId,
  runId,
}) {
  try {
    return await fundingHqFetch(
      `/clients/${encodeURIComponent(clientId)}/browser-profiles/${encodeURIComponent(profileId)}/authorized-qa-proxy/test/cancel?${agencyQuery(agencyId)}`,
      {
        method: 'POST',
        headers: JSON_HEADERS,
        body: serializeAuthorizedQaProxyJsonBody({ run_id: String(runId || '') }),
      },
    );
  } catch (error) {
    throw new Error(getFundingApiErrorMessage(error, 'Unable to stop the connection test.'));
  }
}

/** Browser-side public IP for connection proof. Never use API-server egress. */
export async function fetchBrowserDirectPublicIp({
  fetchImpl = typeof fetch === 'function' ? fetch : null,
  timeoutMs = 8000,
} = {}) {
  if (!fetchImpl) return null;
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = controller
    ? setTimeout(() => controller.abort(), timeoutMs)
    : null;
  try {
    const response = await fetchImpl('https://api.ipify.org?format=json', {
      method: 'GET',
      signal: controller?.signal,
    });
    if (!response?.ok) return null;
    const data = await response.json();
    const ip = String(data?.ip || '').trim();
    return ip || null;
  } catch {
    try {
      const response = await fetchImpl('https://api.ipify.org', {
        method: 'GET',
        signal: controller?.signal,
      });
      if (!response?.ok) return null;
      const text = String(await response.text() || '').trim();
      return text || null;
    } catch {
      return null;
    }
  } finally {
    if (timer) clearTimeout(timer);
  }
}

export function formatConnectionTestTimestamp(value) {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const month = date.toLocaleString('en-US', { month: 'short' });
  const day = date.getDate();
  const time = date.toLocaleString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  return `Verified ${month} ${day} at ${time}`;
}

export async function suspendAuthorizedQaProxy({ clientId, agencyId, profileId, suspend = true }) {
  try {
    return await fundingHqFetch(
      `/clients/${encodeURIComponent(clientId)}/browser-profiles/${encodeURIComponent(profileId)}/authorized-qa-proxy/suspend?${agencyQuery(agencyId)}`,
      {
        method: 'POST',
        headers: JSON_HEADERS,
        body: serializeAuthorizedQaProxyJsonBody({ suspend: Boolean(suspend) }),
      },
    );
  } catch (error) {
    throw new Error(getFundingApiErrorMessage(error, 'Unable to update proxy suspension.'));
  }
}

export async function disconnectAuthorizedQaProxy({ clientId, agencyId, profileId }) {
  try {
    return await fundingHqFetch(
      `/clients/${encodeURIComponent(clientId)}/browser-profiles/${encodeURIComponent(profileId)}/authorized-qa-proxy/disconnect?${agencyQuery(agencyId)}`,
      {
        method: 'POST',
        headers: JSON_HEADERS,
        body: serializeAuthorizedQaProxyJsonBody({}),
      },
    );
  } catch (error) {
    throw new Error(getFundingApiErrorMessage(error, 'Unable to disconnect Authorized QA Proxy.'));
  }
}

export async function issueAuthorizedQaProxyLaunchToken({
  clientId,
  agencyId,
  profileId,
  startupUrl = FIXED_AUTHORIZED_QA_PROXY_STARTUP_URL,
  ttlSeconds = 120,
  sessionDurationMinutes = null,
}) {
  try {
    return await fundingHqFetch(
      `/clients/${encodeURIComponent(clientId)}/browser-profiles/${encodeURIComponent(profileId)}/authorized-qa-proxy/launch-token?${agencyQuery(agencyId)}`,
      {
        method: 'POST',
        headers: JSON_HEADERS,
        body: serializeAuthorizedQaProxyJsonBody({
          startup_url: startupUrl || FIXED_AUTHORIZED_QA_PROXY_STARTUP_URL,
          ttl_seconds: ttlSeconds,
          session_duration_minutes: sessionDurationMinutes,
        }),
      },
    );
  } catch (error) {
    throw new Error(getFundingApiErrorMessage(error, 'Unable to issue proxy launch token.'));
  }
}

/**
 * Build a one-shot shell export that sets only the short-lived token.
 * Never includes host, port, username, or password.
 */
export function buildAuthorizedQaProxyTokenEnvCommand(token, { terminal = 'unix' } = {}) {
  const safe = String(token || '').replace(/'/g, '');
  if (!safe) return '';
  if (terminal === 'windows_powershell') {
    return `$env:FUNDELITE_QA_PROXY_LAUNCH_TOKEN = '${safe}'`;
  }
  if (terminal === 'windows_cmd') {
    return `set "FUNDELITE_QA_PROXY_LAUNCH_TOKEN=${safe}"`;
  }
  return `export FUNDELITE_QA_PROXY_LAUNCH_TOKEN='${safe}'`;
}

/** Explicit launcher kinds — never silently cross Primary and QA. */
export const PRIMARY_LAUNCHER = 'PRIMARY_LAUNCHER';
export const QA_AUTHORIZED_LAUNCHER = 'QA_AUTHORIZED_LAUNCHER';

/** Proof-only env: computer/direct IP for QA post-launch inequality checks. Never geoip/fingerprint. */
export const FUNDELITE_QA_COMPARE_DIRECT_IP_ENV = 'FUNDELITE_QA_COMPARE_DIRECT_IP';

/**
 * QA session launchers must be the isolated authorized_qa_proxy_session script.
 * Never accept Primary (`*_primary.py`) or other non-QA filenames.
 */
export function assertAuthorizedQaProxySessionScriptFilename(scriptFilename) {
  const filename = String(scriptFilename || '').trim();
  if (!filename) {
    throw new Error(
      'QA session launcher filename is missing. Install / Update the QA Browser first.',
    );
  }
  const lower = filename.toLowerCase();
  if (lower.includes('_primary.py') || /(^|[\\/])primary\.py$/.test(lower)) {
    throw new Error(
      'Refusing to launch Primary Browser as a QA session. '
      + 'Use Launch QA Session with the authorized_qa_proxy_session launcher.',
    );
  }
  if (!lower.includes('authorized_qa_proxy_session')) {
    throw new Error(
      'QA session launch requires fcl_<client>_authorized_qa_proxy_session.py '
      + `(got ${filename}).`,
    );
  }
  return filename;
}

/**
 * Direct (non-QA) launchers must never be the authorized QA session script.
 * Primary is `*_primary.py`; backup/other direct profiles may use other non-QA names.
 */
export function assertPrimaryBrowserScriptFilename(scriptFilename) {
  const filename = String(scriptFilename || '').trim();
  if (!filename) {
    throw new Error('Primary Browser launcher filename is missing.');
  }
  const lower = filename.toLowerCase();
  if (lower.includes('authorized_qa_proxy_session')) {
    throw new Error(
      'Refusing to treat the QA session launcher as Primary Browser. '
      + 'Use Launch QA Session for proxied QA.',
    );
  }
  return filename;
}

/**
 * Strict Primary slot: requires `*_primary.py`.
 */
export function assertStrictPrimaryBrowserScriptFilename(scriptFilename) {
  const filename = assertPrimaryBrowserScriptFilename(scriptFilename);
  const lower = filename.toLowerCase();
  if (!lower.includes('_primary.py') && !/(^|[\\/])primary\.py$/.test(lower)) {
    throw new Error(
      `Primary Browser launch requires fcl_<client>_primary.py (got ${filename}).`,
    );
  }
  return filename;
}

function sanitizeCompareDirectIp(value) {
  const ip = String(value || '').trim();
  if (!ip) return '';
  // IPv4 or rough IPv6 — proof comparison only; never embed secrets.
  if (/^(?:\d{1,3}\.){3}\d{1,3}$/.test(ip)) return ip;
  if (ip.includes(':') && /^[0-9a-fA-F:.]+$/.test(ip) && ip.length <= 45) return ip;
  return '';
}

/**
 * Optional proof-only env export for QA post-launch verification.
 * Never used for Camoufox geoip, locale, timezone, or proxy config.
 */
export function buildAuthorizedQaProxyCompareDirectIpEnvCommand(
  directIp,
  { terminal = 'unix' } = {},
) {
  const safe = sanitizeCompareDirectIp(directIp);
  if (!safe) return '';
  if (terminal === 'windows_powershell') {
    return `$env:${FUNDELITE_QA_COMPARE_DIRECT_IP_ENV} = '${safe}'`;
  }
  if (terminal === 'windows_cmd') {
    return `set "${FUNDELITE_QA_COMPARE_DIRECT_IP_ENV}=${safe}"`;
  }
  return `export ${FUNDELITE_QA_COMPARE_DIRECT_IP_ENV}='${safe}'`;
}

/**
 * Complete terminal-specific run command for a one-time proxy launch.
 * Token appears only in this ephemeral command string — never persist it.
 * Always invokes the FundElite-managed Camoufox Python (never system python3).
 * Always targets the QA session launcher — never Primary Browser.
 * Optional clientDirectIp is comparison-only (FUNDELITE_QA_COMPARE_DIRECT_IP).
 * Runs from the home directory where Install / Update writes the launcher, so a session
 * started from any folder reuses the installed launcher and its persistent QA profile.
 */
export const QA_LAUNCH_MISSING_VENV_MESSAGES = Object.freeze([
  'FundElite QA Browser is not installed on this computer.',
  'Use Install / Update QA Browser.',
]);
export const QA_LAUNCH_MISSING_LAUNCHER_MESSAGES = Object.freeze([
  'FundElite QA launcher is missing.',
  'Use Install / Update QA Browser.',
]);
export const QA_LAUNCH_OUTDATED_LAUNCHER_MESSAGES = Object.freeze([
  'The FundElite QA launcher on this computer is out of date.',
  'Use Install / Update QA Browser, then Launch QA Session again.',
]);

/** Current Authorized QA launcher template (see qaBrowserReadiness for the version history). */
export const QA_LAUNCHER_TEMPLATE_VERSION = 'qa-browser-launcher-v23';

/** Oldest launcher that may still run sessions. Raise only for compatibility/security
 * reasons — not for every template bump. v8: earlier launchers end the session (and close
 * the browser) on stdin EOF or a stray Enter; before v7 they also load uBlock Origin, lack
 * the local-protocol guard and loopback routing fix, and carry no on-disk markers. */
export const QA_LAUNCHER_MIN_COMPATIBLE_VERSION = 'qa-browser-launcher-v8';

/** Operator-selectable QA session durations (minutes), provider-scoped. The backend is
 * authoritative: these are filtered by the admin-configured maximum at launch time. */
export const QA_SESSION_DURATION_OPTIONS = Object.freeze({
  dataimpulse: [15, 30, 45, 60],
  '911proxy': [15, 30, 45, 60, 90],
});

/** Client-side convenience only. The server re-validates and can never be bypassed. */
export function getAllowedQaSessionDurations(provider, adminMaxMinutes) {
  const providerKey = String(provider || '').trim().toLowerCase();
  const values = QA_SESSION_DURATION_OPTIONS[providerKey] || [];
  const max = Number(adminMaxMinutes);
  if (!Number.isFinite(max) || max <= 0) return [...values];
  return values.filter((value) => value <= max);
}

export function qaLauncherVersionMarker(version = QA_LAUNCHER_TEMPLATE_VERSION) {
  return `# fundelite-qa-launcher-version: ${version}`;
}

/** Written into every generated QA launcher; the run command refuses files without the
 * marker for the current minimum-compatible version. */
export function qaLauncherCompatibilityMarker(version = QA_LAUNCHER_MIN_COMPATIBLE_VERSION) {
  return `# fundelite-qa-launcher-compatible-with: ${version}`;
}

export function buildAuthorizedQaProxyRunCommand({
  token,
  scriptFilename,
  terminal = 'unix',
  clientDirectIp = null,
} = {}) {
  const envLine = buildAuthorizedQaProxyTokenEnvCommand(token, { terminal });
  if (!envLine) return '';
  const filename = assertAuthorizedQaProxySessionScriptFilename(scriptFilename);
  const compareLine = buildAuthorizedQaProxyCompareDirectIpEnvCommand(clientDirectIp, {
    terminal,
  });
  const [venv1, venv2] = QA_LAUNCH_MISSING_VENV_MESSAGES;
  const [launcher1, launcher2] = QA_LAUNCH_MISSING_LAUNCHER_MESSAGES;
  const [outdated1, outdated2] = QA_LAUNCH_OUTDATED_LAUNCHER_MESSAGES;
  const marker = qaLauncherCompatibilityMarker();
  const prefix = compareLine ? [envLine, compareLine] : [envLine];
  if (terminal === 'windows_powershell') {
    return [
      ...prefix,
      'Set-Location $HOME',
      '$CamoufoxPy = Join-Path $env:USERPROFILE ".fundelite-camoufox-venv\\Scripts\\python.exe"',
      'if (-not (Test-Path -LiteralPath $CamoufoxPy)) {',
      `  Write-Host "${venv1}"`,
      `  Write-Host "${venv2}"`,
      '  exit 1',
      '}',
      `if (-not (Test-Path -LiteralPath "${filename}")) {`,
      `  Write-Host "${launcher1}"`,
      `  Write-Host "${launcher2}"`,
      '  exit 1',
      '}',
      `if (-not (Select-String -LiteralPath "${filename}" -SimpleMatch -Quiet -Pattern "${marker}")) {`,
      `  Write-Host "${outdated1}"`,
      `  Write-Host "${outdated2}"`,
      '  exit 1',
      '}',
      `& $CamoufoxPy -u ${filename}`,
    ].join('\n');
  }
  if (terminal === 'windows_cmd') {
    return [
      ...prefix,
      'cd /d "%USERPROFILE%"',
      'set "CAMOUFOX_PY=%USERPROFILE%\\.fundelite-camoufox-venv\\Scripts\\python.exe"',
      'if not exist "%CAMOUFOX_PY%" (',
      `  echo ${venv1}`,
      `  echo ${venv2}`,
      '  exit /b 1',
      ')',
      `if not exist "${filename}" (`,
      `  echo ${launcher1}`,
      `  echo ${launcher2}`,
      '  exit /b 1',
      ')',
      `findstr /L /C:"${marker}" "${filename}" >nul`,
      'if errorlevel 1 (',
      `  echo ${outdated1}`,
      `  echo ${outdated2}`,
      '  exit /b 1',
      ')',
      `"%CAMOUFOX_PY%" -u ${filename}`,
    ].join('\n');
  }
  return [
    ...prefix,
    'cd "$HOME"',
    'CAMOUFOX_PY="$HOME/.fundelite-camoufox-venv/bin/python"',
    'if [ ! -x "$CAMOUFOX_PY" ]; then',
    `  echo "${venv1}"`,
    `  echo "${venv2}"`,
    '  exit 1',
    'fi',
    `if [ ! -f "${filename}" ]; then`,
    `  echo "${launcher1}"`,
    `  echo "${launcher2}"`,
    '  exit 1',
    'fi',
    `if ! grep -qF "${marker}" "${filename}"; then`,
    `  echo "${outdated1}"`,
    `  echo "${outdated2}"`,
    '  exit 1',
    'fi',
    `"$CAMOUFOX_PY" -u \\`,
    `  ${filename}`,
  ].join('\n');
}

/** Countdown for the launch-token modal, e.g. "04:58"; "00:00" once expired. */
export function formatProxyLaunchTokenCountdown(expiresAt, now = Date.now()) {
  const expiresMs = Date.parse(expiresAt || '');
  if (Number.isNaN(expiresMs)) return '';
  const totalSeconds = Math.max(0, Math.ceil((expiresMs - now) / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

export function formatProxyLaunchTokenExpiryLabel(expiresAt, now = Date.now()) {
  if (!expiresAt) return 'Expiration unavailable';
  const expiresMs = Date.parse(expiresAt);
  if (Number.isNaN(expiresMs)) return 'Expiration unavailable';
  const remainingMs = expiresMs - now;
  if (remainingMs <= 0) return 'Expired';
  const totalSeconds = Math.ceil(remainingMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes > 0) {
    return `Expires in ${minutes}m ${String(seconds).padStart(2, '0')}s`;
  }
  return `Expires in ${seconds}s`;
}

export function assertNoProxySecretsInText(text, secrets = []) {
  const haystack = String(text || '');
  for (const secret of secrets) {
    if (secret && haystack.includes(String(secret))) {
      throw new Error('Proxy secret leaked into generated text');
    }
  }
  return true;
}
